import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { Text } from 'preact-i18n';
import { useRef, useState } from 'preact/hooks';
import cx from 'classnames';
import style from './style.css';
import { getDragAndDropBackend } from '../../utils/dragAndDropBackend';

const DEVICE_TYPE = 'DEVICE_TYPE';

const DeviceRow = ({
  selectedDeviceFeature,
  moveDevice,
  index,
  removeDevice,
  updateDeviceFeatureName,
  governingFeatures = [],
  enabledBy,
  updateEnabledBy
}) => {
  const ref = useRef(null);
  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: DEVICE_TYPE,
    item: () => {
      return { index };
    },
    collect: monitor => ({
      isDragging: !!monitor.isDragging()
    })
  }));
  const [{ isActive }, drop] = useDrop({
    accept: DEVICE_TYPE,
    collect: monitor => ({
      isActive: monitor.canDrop() && monitor.isOver()
    }),
    drop(item) {
      if (!ref.current) {
        return;
      }
      moveDevice(item.index, index);
    }
  });
  preview(drop(ref));
  const removeThisDevice = () => {
    removeDevice(index);
  };

  const [isEmpty, setIsEmpty] = useState(selectedDeviceFeature.new_label === '');
  const updateThisDeviceFeatureName = e => {
    updateDeviceFeatureName(index, e.target.value);
    setIsEmpty(e.target.value === '');
  };

  const emptyFieldAutogenerated = isEmpty && selectedDeviceFeature.new_label !== '';

  // A row cannot govern itself: that is a rule that can never be satisfied.
  const others = governingFeatures.filter(f => f.selector !== selectedDeviceFeature.value);
  const governingSelector = (enabledBy && enabledBy.feature) || '';
  const governing = others.find(f => f.selector === governingSelector);
  const governingValue = (enabledBy && enabledBy.value) || '';
  const onGoverningChange = e => {
    const feature = e.target.value;
    const first = governingFeatures.find(f => f.selector === feature);
    // A value is chosen with the feature, so a half-written rule is never
    // saved: without one the row would be governed by a comparison with ''.
    updateEnabledBy(selectedDeviceFeature.value, {
      feature,
      value: feature && first && first.values.length ? first.values[first.values.length - 1].value : ''
    });
  };
  const onValueChange = e => updateEnabledBy(selectedDeviceFeature.value, { value: e.target.value });
  return (
    <div class="mb-1">
      <label class="form-label">{selectedDeviceFeature.label}</label>
      <div
        class={cx('input-group', style.deviceListDragAndDrop, {
          [style.deviceListDragAndDropActive]: isActive,
          [style.deviceListDragAndDropDragging]: isDragging
        })}
        ref={ref}
      >
        <div class="input-group-prepend" ref={drag}>
          <span class="input-group-text fe fe-list" />
        </div>
        <input
          type="text"
          class={cx('form-control', {
            [style.deviceListDragAndDropEmpty]: isEmpty && !emptyFieldAutogenerated,
            [style.deviceListDragAndDropEmptyAutoGenerated]: emptyFieldAutogenerated,
            [style.deviceListDragAndDropActive]: isActive
          })}
          value={selectedDeviceFeature.new_label || ''}
          placeholder={selectedDeviceFeature.label}
          onChange={updateThisDeviceFeatureName}
        />
        <div class={cx('input-group-append', style.deviceListRemoveButton)}>
          <button class="btn btn-outline-danger" type="button" onClick={removeThisDevice}>
            <span class=" fe fe-x" />
          </button>
        </div>
      </div>
      {/*
        Only offered when there is something to be governed BY. On a box with
        one feature the pair would be two empty selects and a question nobody
        can answer.
      */}
      {others.length > 0 && (
        <div class="d-flex align-items-center mt-1">
          <small class="text-muted mr-2">
            <Text id="dashboard.boxes.devices.enabledByLabel" />
          </small>
          <select class="form-control form-control-sm mr-2" value={governingSelector} onChange={onGoverningChange}>
            <option value="">
              <Text id="dashboard.boxes.devices.enabledByAlways" />
            </option>
            {others.map(other => (
              <option key={other.selector} value={other.selector} selected={other.selector === governingSelector}>
                {other.label}
              </option>
            ))}
          </select>
          {governingSelector && (
            <select class="form-control form-control-sm" value={governingValue} onChange={onValueChange}>
              {(governing ? governing.values : []).map(v => (
                <option key={v.value} value={v.value} selected={v.value === governingValue}>
                  {v.label}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
    </div>
  );
};

const { backend: dragAndDropBackend, options: dragAndDropBackendOptions } = getDragAndDropBackend();

const DeviceListWithDragAndDrop = ({
  selectedDeviceFeaturesOptions,
  moveDevice,
  removeDevice,
  updateDeviceFeatureName,
  governingFeatures,
  featureEnabledBy = {},
  updateEnabledBy
}) => (
  <DndProvider backend={dragAndDropBackend} options={dragAndDropBackendOptions}>
    {selectedDeviceFeaturesOptions.map((selectedDeviceFeature, index) => (
      <DeviceRow
        selectedDeviceFeature={selectedDeviceFeature}
        updateDeviceFeatureName={updateDeviceFeatureName}
        index={index}
        moveDevice={moveDevice}
        removeDevice={removeDevice}
        governingFeatures={governingFeatures}
        enabledBy={featureEnabledBy[selectedDeviceFeature.value]}
        updateEnabledBy={updateEnabledBy}
      />
    ))}
  </DndProvider>
);

export { DeviceListWithDragAndDrop };
